import clsx from 'clsx'
import { useState, type ReactNode } from 'react'
import { View, type ViewProps } from 'react-native'

import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTimeout } from '@/src/general/hooks/useTimeout'

import { Icon } from '../media/Icon'

export type TCheckstampProps = {
  state?: 'loading' | 'empty' | 'valid' | 'waiting'
  checkAnimationDelay?: number | null
  size?: 'sm' | 'md'
} & Pick<ViewProps, 'aria-checked'>

const bgColorClass = (state: TCheckstampProps['state']) => {
  switch (state) {
    case 'loading':
      return 'bg-default-bg-loading'
    case 'empty':
      return 'bg-transparent'
    case 'valid':
      return 'bg-success-bg-inverse'
    case 'waiting':
      return 'bg-default-bg-light'
  }
}

export const CheckStamp = ({
  state = 'loading',
  checkAnimationDelay = null,
  size = 'md',
  ...otherProps
}: TCheckstampProps): ReactNode => {
  const [isVisible, setIsVisible] = useState(false)

  useTimeout(() => setIsVisible(true), checkAnimationDelay)

  const isValid = state === 'valid'
  const isValidOrWaiting = isValid || state === 'waiting'

  return (
    <View
      testID={TEST_ID.ELEMENT.STEP}
      className={clsx(
        'rounded-full transition-all duration-300 ease-[cubic-bezier(0,2,0.5,1)]',
        bgColorClass(state),
        isValidOrWaiting && 'p-1',
        isValid && size === 'sm' && 'p-[2px]',
        state === 'valid' && !isVisible && 'scale-0 opacity-0',
      )}
      {...otherProps}
    >
      {state === 'loading' && <View className="h-8 w-8 rounded-full border-default-icon-contrast-lower" />}
      {state === 'empty' && size === 'md' && <Icon size="xl" name={ICON.WAIT_CIRCLE} />}
      {state === 'empty' && size === 'sm' && (
        <View className="h-6 w-6 rounded-full bg-white border-default-bg-high border-2" />
      )}
      {state === 'valid' && <Icon size={size} className="text-white" name={ICON.CHECK} />}
      {state === 'waiting' && <Icon className="text-default-icon-contrast-lower" name={ICON.HOURGLASS_HALF} />}
    </View>
  )
}
